import { useMemo, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { Steps } from 'primereact/steps';
import { Button } from 'primereact/button';
import type { MenuItem } from 'primereact/menuitem';

import { RootState } from '../../../store';
import {
  useGetCategoryQuery,
  useGetManagementQuery,
  useGetPanelQuery,
  useGetStaffQuery,
  useGetSubjectQuery,
  useGetPriorityMasterQuery,
  useGetOrderCopyGridMasterQuery,
  usePostPlanCounsellingMutation,
  usePostCnsnPriorityMutation,
  usePostOrderTemplateMutation,
  useLazyEditCounsellingQuery,
  useLazyGetCnsnPriorityQuery,
  useLazyGetOrderTemplateQuery,
} from '../counselling.services';
import { alertService } from '../../../shared/services/alertService';
import { useBreadcrumb } from '../../../layouts/BreadcrumbContext';
import { DIRECTORATE_LIST } from '../../../shared/services/defaultValues';
import { toApiDate, displayDate } from './dateUtils';
import {
  DetailsForm,
  OrderTemplate,
  Option,
  StaffEntry,
  PriorityEntry,
  PriorityRemoveEntry,
  SeniorityEntry,
  TableGridEntry,
  CounsellingListRow,
} from './types';
import {
  isScertRole,
  resolveInitialOptions,
  resolveOptions,
} from './planningOptions';
import {
  PlanningWizardContext,
  PlanningWizardValue,
} from './PlanningWizardContext';
import { PlanningLanding } from './PlanningLanding';
import StepDetails from './steps/StepDetails';
import StepStaff from './steps/StepStaff';
import StepCategoryPanel from './steps/StepCategoryPanel';
import StepApproval from './steps/StepApproval';
import StepPriority from './steps/StepPriority';
import StepTableGrid from './steps/StepTableGrid';
import StepOrderTemplate from './steps/StepOrderTemplate';
import StepSeniority from './steps/StepSeniority';
import StepConfirmation from './steps/StepConfirmation';
import NoStepPlaceholder from './steps/NoStepPlaceholder';

/* eslint-disable @typescript-eslint/no-explicit-any */

const str = (v: any): string => (v == null ? '' : String(v));

const EMPTY_DETAILS: DetailsForm = {
  FromDate: null,
  ToDate: null,
  Title: '',
  CounsellingType: '',
  Directorate: '',
  Location: '',
  CandidateUpdatedBy: '',
};

const EMPTY_ORDER_TEMPLATE: OrderTemplate = {
  IndexId: '',
  Type: '',
  Header: '',
  Reference: '',
  Reference2: '',
  ContentBefore: '',
  ContentAfter: '',
  CopyTo: '',
  SignAuth: '',
};

/**
 * Plan New Counselling — migrated from Angular PlanNewCounsellingComponent.
 * Single page that toggles between a landing list and a 10-step wizard
 * (Angular IsAddEditPage). Cross-step state lives here and is shared with the
 * step components via PlanningWizardContext.
 */
const PlanNewCounselling = () => {
  const session = useSelector((s: RootState) => s.session);
  const { setSubItems, clearSubItems } = useBreadcrumb();
  const userType = str(session.usertypeid);
  const userType1 = str(session.emisUsertype1);
  const isSCERT = isScertRole({ userType, userType1 });

  // ── Page mode ──────────────────────────────────────────────────────────────
  const [isAddEditPage, setIsAddEditPage] = useState(false);
  const [heading, setHeading] = useState('Add New Counselling');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isEdit, setIsEdit] = useState(false);
  const [counsellingId, setCounsellingId] = useState('');

  // ── Master data (static JSON; key resolved per role/directorate below) ──────
  const { data: categoryRaw } = useGetCategoryQuery();
  const { data: managementRaw } = useGetManagementQuery();
  const { data: panelRaw } = useGetPanelQuery();
  const { data: staffRaw } = useGetStaffQuery();
  const { data: subjectRaw } = useGetSubjectQuery();
  const { data: priorityMasterRaw, refetch: refetchPriorityMaster } =
    useGetPriorityMasterQuery();
  const { data: gridMasterRaw } = useGetOrderCopyGridMasterQuery();
  const [postPlanCounselling] = usePostPlanCounsellingMutation();
  const [postCnsnPriority] = usePostCnsnPriorityMutation();
  const [postOrderTemplate] = usePostOrderTemplateMutation();
  const [triggerEditCounselling] = useLazyEditCounsellingQuery();
  const [triggerCnsnPriority] = useLazyGetCnsnPriorityQuery();
  const [triggerOrderTemplate] = useLazyGetOrderTemplateQuery();

  // ── Step 1 form ─────────────────────────────────────────────────────────────
  const detailsForm = useFormik<DetailsForm>({
    initialValues: EMPTY_DETAILS,
    onSubmit: () => undefined,
  });
  const [locationDisable, setLocationDisable] = useState(false);

  // ── Step 2 ──────────────────────────────────────────────────────────────────
  const [staffList, setStaffList] = useState<StaffEntry[]>([]);
  const [staffType, setStaffType] = useState('');
  const [subject, setSubject] = useState('');
  const [staffDisable, setStaffDisable] = useState(false);

  // ── School type / minority ───────────────────────────────────────────────────
  const [schType, setSchType] = useState('');
  const [counsellingMinority, setCounsellingMinority] = useState('');
  const [vacancyMinority, setVacancyMinority] = useState('');

  // ── Step 3 ──────────────────────────────────────────────────────────────────
  const [selectedSchCategory, setSelectedSchCategory] = useState<string[]>([]);
  const [selectedSchManage, setSelectedSchManage] = useState<string[]>([]);
  const [selectedSchPanel, setSelectedSchPanel] = useState<string[]>([]);

  // ── Step 4 ──────────────────────────────────────────────────────────────────
  const [vacUpdatedBy, setVacUpdatedBy] = useState('');
  const [selectedVacCategory, setSelectedVacCategory] = useState<string[]>([]);
  const [selectedVacManage, setSelectedVacManage] = useState<string[]>([]);
  const [selectedVacPanel, setSelectedVacPanel] = useState<string[]>([]);

  // ── Step 5 ──────────────────────────────────────────────────────────────────
  const [level1Approver, setLevel1Approver] = useState('');
  const [level2Approver, setLevel2Approver] = useState('');

  // ── Step 6 ──────────────────────────────────────────────────────────────────
  const [priorityList, setPriorityList] = useState<Option[]>([]);
  const [addedPriorityList, setAddedPriorityList] = useState<PriorityEntry[]>(
    []
  );
  const [removeList, setRemoveList] = useState<PriorityRemoveEntry[]>([]);

  // ── Step 7 ──────────────────────────────────────────────────────────────────
  const [tableValueList, setTableValueList] = useState<Option[]>([]);
  const [addedTableGridList, setAddedTableGridList] = useState<
    TableGridEntry[]
  >([]);

  // ── Step 8 ──────────────────────────────────────────────────────────────────
  const orderTemplate = useFormik<OrderTemplate>({
    initialValues: EMPTY_ORDER_TEMPLATE,
    onSubmit: () => undefined,
  });
  const [orderArray, setOrderArray] = useState<OrderTemplate[]>([]);

  // ── Step 9 ──────────────────────────────────────────────────────────────────
  const [addedSeniorityList, setAddedSeniorityList] = useState<
    SeniorityEntry[]
  >([]);

  // ── Derived master lists (role/directorate keyed) ───────────────────────────
  const directorate = str(detailsForm.values.Directorate);
  const counsellingType = str(detailsForm.values.CounsellingType);

  const allDirectorate: Option[] = DIRECTORATE_LIST;

  const categoryList: any[] = useMemo(() => {
    if (!categoryRaw) return [];
    return isSCERT ? categoryRaw.SCERTCategory : categoryRaw.Category;
  }, [categoryRaw, isSCERT]);

  const allOrderList: SeniorityEntry[] = useMemo(() => {
    if (!categoryRaw) return [];
    return isSCERT
      ? (categoryRaw.ScertSeniorityOrderList ?? [])
      : (categoryRaw.SeniorityOrderList ?? []);
  }, [categoryRaw, isSCERT]);
  const [orderList, setOrderList] = useState<SeniorityEntry[]>([]);

  const managementList: any[] = useMemo(() => {
    if (!managementRaw) return [];
    if (isSCERT) return managementRaw.SCERTManagement;
    if (schType === '2') return managementRaw.OfficeManagement;
    return managementRaw.Management;
  }, [managementRaw, isSCERT, schType]);

  const panelList: any[] = useMemo(() => {
    if (!panelRaw) return [];
    return directorate === '5' ? panelRaw.ScertPanel : panelRaw.Panel;
  }, [panelRaw, directorate]);

  const staffTypeList: Option[] = useMemo(() => {
    if (!staffRaw) return [];
    if (isSCERT) return staffRaw.SCERTStaffType;
    if (directorate === '2') return staffRaw.DSEStaffType;
    return staffRaw.AllStaffType;
  }, [staffRaw, isSCERT, directorate]);

  // Subject list depends on the chosen staff type (Angular handleStaffType).
  const isShowSubject = ['11', '103', '7', '36', '139', '138'].includes(
    staffType
  );
  const subjectList: Option[] = useMemo(() => {
    if (!subjectRaw || !isShowSubject) return [];
    if (staffType === '11' || staffType === '260')
      return subjectRaw.BTStaffList;
    if (staffType === '103') return subjectRaw.BRTEStaffList;
    if (staffType === '7') return subjectRaw.PartTimeStaffList;
    if (staffType === '36') return subjectRaw.PGStaffList;
    if (staffType === '139' || staffType === '138')
      return subjectRaw.LecturerAndJunrLectrStfLst;
    return subjectRaw.AllSubject;
  }, [subjectRaw, staffType, isShowSubject]);

  const allPriorityList: Option[] = useMemo(() => {
    if (!priorityMasterRaw?.dataStatus) return [];
    return (priorityMasterRaw.result ?? []).map((mp: any) => ({
      label: mp.Priority,
      value: mp.IndexId,
    }));
  }, [priorityMasterRaw]);

  // When the priority master is re-fetched (e.g. after adding a new priority),
  // add any newly-arrived items to the dropdown list — skipping ones already
  // in the list or already committed to the added-priority table.
  useEffect(() => {
    if (allPriorityList.length === 0) return;
    setPriorityList((prev) => {
      const used = new Set([
        ...prev.map((p) => p.value),
        ...addedPriorityList.map((p) => p.value),
      ]);
      const missing = allPriorityList.filter((p) => !used.has(p.value));
      return missing.length > 0 ? [...prev, ...missing] : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPriorityList]);

  const allTableValueList: Option[] = useMemo(() => {
    if (!gridMasterRaw?.dataStatus) return [];
    return (gridMasterRaw.result ?? []).map((mp: any) => ({
      label: mp.display_name,
      value: mp.column_name,
    }));
  }, [gridMasterRaw]);

  // ── Options resolver (role + directorate + staffType) ───────────────────────
  const options = useMemo(
    () =>
      isAddEditPage && directorate
        ? resolveOptions({ userType, userType1, directorate, staffType })
        : resolveInitialOptions({ userType, userType1 }),
    [isAddEditPage, directorate, staffType, userType, userType1]
  );

  // ── Navigation ──────────────────────────────────────────────────────────────
  const nextPage = useCallback(() => setActiveIndex((i) => i + 1), []);
  const previousPage = useCallback(() => setActiveIndex((i) => i - 1), []);
  const goToStep = useCallback((i: number) => setActiveIndex(i), []);

  const refreshPriorityMaster = useCallback(() => {
    refetchPriorityMaster();
  }, [refetchPriorityMaster]);

  // ── Steps model ─────────────────────────────────────────────────────────────
  const items: MenuItem[] = useMemo(
    () => [
      { label: 'Date' },
      { label: 'Staff Type' },
      { label: isSCERT ? 'Institution Category' : 'School Category' },
      { label: 'Vacancies' },
      { label: 'Approval' },
      { label: 'Priority' },
      { label: 'Table Grid' },
      { label: 'Order Template' },
      { label: 'Seniority Order' },
      { label: 'Confirmation' },
    ],
    [isSCERT]
  );

  // ── Enter add mode (Angular handleAddNewCoun) ───────────────────────────────
  const handleAddNew = useCallback(() => {
    setHeading('Add New Counselling');
    setIsEdit(false);
    setCounsellingId('');
    setActiveIndex(0);
    detailsForm.resetForm({ values: EMPTY_DETAILS });
    orderTemplate.resetForm({ values: EMPTY_ORDER_TEMPLATE });
    setStaffList([]);
    setStaffType('');
    setSubject('');
    setStaffDisable(false);
    setSchType('');
    setCounsellingMinority('');
    setVacancyMinority('');
    setSelectedSchCategory([]);
    setSelectedSchPanel([]);
    setSelectedVacCategory([]);
    setSelectedVacPanel([]);
    setSelectedVacManage(userType1 === '29' ? ['2'] : []);
    setSelectedSchManage(userType1 === '29' ? ['2'] : []);
    setVacUpdatedBy('');
    setLevel1Approver('');
    setLevel2Approver('');
    setAddedPriorityList([
      { label: '', value: '', IndexId: '', IsActive: '', PriorityNo: '' },
    ]);
    setAddedTableGridList([
      { key: '', value: [], valueLabel: '', orderId: '' },
    ]);
    setAddedSeniorityList([{ label: '', value: '', OrderKey: '' }]);
    setPriorityList(allPriorityList);
    setTableValueList(allTableValueList);
    setOrderList(allOrderList);
    setRemoveList([]);
    setIsAddEditPage(true);
  }, [
    detailsForm,
    orderTemplate,
    userType1,
    allPriorityList,
    allTableValueList,
    allOrderList,
  ]);

  const handleCancel = useCallback(() => setIsAddEditPage(false), []);

  // Drive the app-shell breadcrumb: in the wizard show
  // "Plan New Counselling / {heading}"; on the landing list, clear it.
  useEffect(() => {
    if (isAddEditPage) {
      setSubItems([{ label: heading }]);
    } else {
      clearSubItems();
    }
    return () => clearSubItems();
  }, [isAddEditPage, heading, handleCancel, setSubItems, clearSubItems]);

  // confirmationDetails is now derived inside StepConfirmation, so this is a
  // no-op kept for the step submit handlers that still call it (Angular parity).
  const confirmationDetails = useCallback(() => undefined, []);

  // Load a saved order template into the form (Angular getOrderTem). Replaces
  // the P11 stub; used on tab switch and edit-load.
  const loadOrderTemplate = useCallback(
    async (type: number) => {
      if (!counsellingId) return;
      try {
        const res: any = await triggerOrderTemplate({
          counsellingId,
          type,
        }).unwrap();
        if (res?.dataStatus) {
          const match = (res.result ?? []).filter(
            (ft: any) => String(ft.Type) === String(type)
          );
          if (match.length > 0) {
            orderTemplate.setValues({ ...EMPTY_ORDER_TEMPLATE, ...match[0] });
          } else {
            orderTemplate.resetForm({ values: EMPTY_ORDER_TEMPLATE });
          }
        } else {
          orderTemplate.resetForm({ values: EMPTY_ORDER_TEMPLATE });
        }
      } catch {
        orderTemplate.resetForm({ values: EMPTY_ORDER_TEMPLATE });
      }
    },
    [counsellingId, triggerOrderTemplate, orderTemplate]
  );

  // Edit an existing counselling (Angular handleEditCoun + GetOneCounselling +
  // GetOnePriority). RTK caches the master JSON already, so we sequence with
  // async/await instead of Angular's setTimeout hacks.
  const handleEditCoun = useCallback(
    async (row: CounsellingListRow) => {
      const id = String(row.IndexId);
      setHeading('Update Counselling');
      setIsEdit(true);
      setCounsellingId(id);
      setActiveIndex(0);
      setRemoveList([]);
      setIsAddEditPage(true);

      const subjectLookup: Option[] = subjectRaw?.AllSubject ?? [];
      const staffLookup: Option[] = staffRaw?.AllStaffType ?? [];

      try {
        const res: any = await triggerEditCounselling({
          counsellingId: id,
        }).unwrap();
        const r = res?.dataStatus ? res.result?.[0] : null;
        if (!r) {
          alertService.error('Something went wrong');
          return;
        }

        detailsForm.setValues({
          FromDate: displayDate(r.FromDate),
          ToDate: displayDate(r.ToDate),
          Title: r.Title ?? '',
          CounsellingType: str(r.CounsellingType),
          Directorate: str(r.Directorate),
          Location: str(r.Location),
          CandidateUpdatedBy: str(r.CandidateUpdatedBy),
        });
        setStaffType(str(r.Designation));
        setLevel1Approver(str(r.Level1Approver));
        setLevel2Approver(str(r.Level2Approver));
        setVacUpdatedBy(str(r.VacUpdatedBy));
        setSchType(str(r.schType));
        setCounsellingMinority(str(r.counsellingMinority));
        setVacancyMinority(str(r.vacancyMinority));

        // Staff list from the CSV Subject list (or a single subject-less row).
        const desig = staffLookup.filter((s) => s.value === str(r.Designation));
        const desigName = desig.length > 0 ? desig[0].label : r.DesignationName;
        if (r.Subject) {
          const subs = String(r.Subject).split(',');
          setStaffList(
            subs.map((sid) => {
              const sm = subjectLookup.filter((s) => s.value === sid);
              return {
                Designation: str(r.Designation),
                DesignationName: desigName ?? '',
                Subject: sid,
                SubjectName: sm.length > 0 ? sm[0].label : sid,
              };
            })
          );
        } else {
          setStaffList([
            {
              Designation: str(r.Designation),
              DesignationName: desigName ?? '',
              Subject: null,
              SubjectName: null,
            },
          ]);
        }
        setStaffDisable(!!r.Designation);

        const split = (v: any): string[] => (v ? String(v).split(',') : []);
        setSelectedSchCategory(split(r.SclCategory));
        setSelectedSchManage(split(r.SclManagement));
        setSelectedSchPanel(split(r.SclPanel));
        setSelectedVacCategory(split(r.VacCategory));
        setSelectedVacManage(split(r.VacManagement));
        setSelectedVacPanel(split(r.VacPanel));

        // Table grid.
        let grid: TableGridEntry[] = r.order_copy_grid
          ? JSON.parse(r.order_copy_grid)
          : [];
        grid = [...grid, { key: '', value: [], valueLabel: '', orderId: '' }];
        setAddedTableGridList(grid);
        const usedGridValues = grid
          .flatMap((g) => g.value || [])
          .map((x) => String(x));
        setTableValueList(
          allTableValueList.filter((o) => !usedGridValues.includes(o.value))
        );

        // Seniority order.
        const snty = r.SeniorityOrder
          ? String(r.SeniorityOrder).split(',')
          : [];
        const seniority: SeniorityEntry[] = [];
        snty.forEach((k) => {
          const m = allOrderList.filter((o) => o.OrderKey === k);
          if (m.length > 0) seniority.push(m[0]);
        });
        seniority.push({ label: '', value: '', OrderKey: '' });
        setAddedSeniorityList(seniority);
        setOrderList(
          allOrderList.filter(
            (o) => !seniority.some((e) => e.value === o.value)
          )
        );
      } catch {
        alertService.error('Something went wrong');
        return;
      }

      // Priority (Angular GetOnePriority edit mode).
      try {
        const pres: any = await triggerCnsnPriority({
          counsellingId: id,
        }).unwrap();
        let list: PriorityEntry[] = [];
        if (pres?.dataStatus) {
          list = (pres.result ?? []).map((mp: any) => ({
            label: mp.priority,
            value: mp.PriorityId,
            IndexId: mp.IndexId,
            IsActive: 1,
            PriorityNo: mp.PriorityNo,
          }));
          list.sort((a, b) => (+a.PriorityNo < +b.PriorityNo ? -1 : 1));
        }
        list.push({
          label: '',
          value: '',
          IndexId: '',
          IsActive: '',
          PriorityNo: '',
        });
        setAddedPriorityList(list);
        setPriorityList(
          allPriorityList.filter((o) => !list.some((e) => e.value === o.value))
        );
      } catch {
        setAddedPriorityList([
          { label: '', value: '', IndexId: '', IsActive: '', PriorityNo: '' },
        ]);
      }

      // Load the order template for the first tab.
      loadOrderTemplate(1);
    },
    [
      subjectRaw,
      staffRaw,
      triggerEditCounselling,
      triggerCnsnPriority,
      detailsForm,
      allTableValueList,
      allOrderList,
      allPriorityList,
      loadOrderTemplate,
    ]
  );

  // Priority diff post (Angular priorityApiSubmit): active priorities + removed
  // ones, filtered to the exact 5-key shape the API expects.
  const priorityApiSubmit = useCallback(
    async (cnsId: string) => {
      const dataArray = addedPriorityList
        .filter((p) => p.label)
        .map((p, i) => ({
          CnsId: cnsId,
          PriorityId: p.value,
          PriorityNo: i + 1,
          IndexId: p.IndexId,
          IsActive: p.IsActive,
        }));
      const withRemove: any[] = [...dataArray, ...removeList];
      const fitrAry = withRemove.filter((r) => Object.keys(r).length === 5);
      try {
        await postCnsnPriority({ records: fitrAry }).unwrap();
      } catch {
        /* non-fatal — matches Angular which ignores priority post errors */
      }
    },
    [addedPriorityList, removeList, postCnsnPriority]
  );

  // Order-template post (Angular handleOrderTemplate).
  const handleOrderTemplateSubmit = useCallback(
    async (cnsId: string) => {
      let records: OrderTemplate[];
      if (counsellingType === '3') {
        records = orderArray.map((o) => ({ ...o, CnslId: cnsId }));
      } else {
        records = [{ ...orderTemplate.values, Type: '1', CnslId: cnsId }];
      }
      try {
        await postOrderTemplate({ records }).unwrap();
      } catch {
        /* non-fatal */
      }
    },
    [counsellingType, orderArray, orderTemplate, postOrderTemplate]
  );

  // Final submit (Angular finalSubmit) — posts the plan, then priority + order
  // template, then returns to the landing list.
  const finalSubmit = useCallback(async () => {
    const sub = staffList.map((s) => s.Subject).filter(Boolean);
    const orderKeys = addedSeniorityList
      .filter((s) => s.OrderKey)
      .map((s) => s.OrderKey);
    const gridRows = addedTableGridList.filter((r) => r.key);

    const record = {
      IndexId: counsellingId || '',
      FromDate: toApiDate(detailsForm.values.FromDate),
      ToDate: toApiDate(detailsForm.values.ToDate),
      Title: detailsForm.values.Title,
      CounsellingType: detailsForm.values.CounsellingType,
      CandidateUpdatedBy: detailsForm.values.CandidateUpdatedBy,
      Location: detailsForm.values.Location,
      Directorate: detailsForm.values.Directorate,
      Subject: sub.toString(),
      TeacherType: staffList[0]?.Designation ?? '',
      DesignationName: staffList[0]?.DesignationName ?? '',
      schType,
      counselling_minority: counsellingMinority,
      SclCategory: selectedSchCategory.toString(),
      SclManagement: selectedSchManage.toString(),
      SclPanel: selectedSchPanel.toString(),
      VacUpdatedBy: vacUpdatedBy,
      vacancy_minority: vacancyMinority,
      VacCategory: selectedVacCategory.toString(),
      VacManagement: selectedVacManage.toString(),
      VacPanel: selectedVacPanel.toString(),
      Level1Approver: level1Approver,
      Level2Approver: level2Approver,
      AppSts: '2',
      VacanyPublish: '2',
      SeniorityPublish: '2',
      VacancyChallenge: '2',
      SeniorityChallenge: '2',
      PreSelectVacancies: '2',
      UpdateCandidate: '2',
      SeniorityOrder: orderKeys.toString(),
      SeniorityReset: '1',
      order_copy_grid: JSON.stringify(gridRows),
    };

    try {
      const res: any = await postPlanCounselling({ records: record }).unwrap();
      if (res?.dataStatus) {
        const newId = String(res.IndxID ?? counsellingId ?? '');
        await priorityApiSubmit(newId);
        await handleOrderTemplateSubmit(newId);
        alertService.success('Submitted successfully');
        setIsAddEditPage(false);
      } else {
        alertService.error(res.message);
      }
    } catch {
      alertService.error('Something went wrong. Please try again');
    }
  }, [
    staffList,
    addedSeniorityList,
    addedTableGridList,
    counsellingId,
    detailsForm,
    schType,
    counsellingMinority,
    selectedSchCategory,
    selectedSchManage,
    selectedSchPanel,
    vacUpdatedBy,
    vacancyMinority,
    selectedVacCategory,
    selectedVacManage,
    selectedVacPanel,
    level1Approver,
    level2Approver,
    postPlanCounselling,
    priorityApiSubmit,
    handleOrderTemplateSubmit,
  ]);

  const wizardValue: PlanningWizardValue = {
    userType,
    userType1,
    isSCERT,
    isEdit,
    counsellingId,
    detailsForm,
    options,
    locationDisable,
    setLocationDisable,
    categoryList,
    managementList,
    panelList,
    staffTypeList,
    subjectList,
    allDirectorate,
    orderList,
    setOrderList,
    allOrderList,
    staffList,
    setStaffList,
    staffType,
    setStaffType,
    subject,
    setSubject,
    isShowSubject,
    staffDisable,
    setStaffDisable,
    schType,
    setSchType,
    counsellingMinority,
    setCounsellingMinority,
    vacancyMinority,
    setVacancyMinority,
    selectedSchCategory,
    setSelectedSchCategory,
    selectedSchManage,
    setSelectedSchManage,
    selectedSchPanel,
    setSelectedSchPanel,
    vacUpdatedBy,
    setVacUpdatedBy,
    selectedVacCategory,
    setSelectedVacCategory,
    selectedVacManage,
    setSelectedVacManage,
    selectedVacPanel,
    setSelectedVacPanel,
    level1Approver,
    setLevel1Approver,
    level2Approver,
    setLevel2Approver,
    priorityList,
    setPriorityList,
    allPriorityList,
    addedPriorityList,
    setAddedPriorityList,
    removeList,
    setRemoveList,
    refreshPriorityMaster,
    tableValueList,
    setTableValueList,
    allTableValueList,
    addedTableGridList,
    setAddedTableGridList,
    orderTemplate,
    orderArray,
    setOrderArray,
    loadOrderTemplate,
    addedSeniorityList,
    setAddedSeniorityList,
    activeIndex,
    nextPage,
    previousPage,
    goToStep,
    finalSubmit,
    confirmationDetails,
  };

  if (!isAddEditPage) {
    return (
      <PlanningLanding
        userType={userType}
        userType1={userType1}
        allDirectorate={allDirectorate}
        onAddNew={handleAddNew}
        onEdit={handleEditCoun}
      />
    );
  }

  return (
    <PlanningWizardContext.Provider value={wizardValue}>
      <div className="main-body-content plan-new-counselling">
        <div className="row">
          <div className="col text-end">
            <Button
              type="button"
              icon="pi pi-arrow-left"
              label="Back"
              className="p-button-danger"
              onClick={handleCancel}
            />
          </div>
        </div>
        <br />

        <div className="card p-4">
          <Steps
            model={items}
            activeIndex={activeIndex}
            readOnly={false}
            onSelect={(e) => goToStep(e.index)}
          />
        </div>

        <div className="mt-4">
          {activeIndex === 0 && <StepDetails />}
          {activeIndex === 1 && <StepStaff />}
          {activeIndex === 2 &&
            (counsellingType === '4' ? (
              <NoStepPlaceholder message="No School Category" />
            ) : (
              <StepCategoryPanel mode="school" />
            ))}
          {activeIndex === 3 && <StepCategoryPanel mode="vacancy" />}
          {activeIndex === 4 &&
            (['3', '4', '5', '6', '7'].includes(counsellingType) ? (
              <NoStepPlaceholder message="No Approval" />
            ) : (
              <StepApproval />
            ))}
          {activeIndex === 5 &&
            (['3', '4', '5', '6', '7'].includes(counsellingType) ? (
              <NoStepPlaceholder message="No Priority" />
            ) : (
              <StepPriority />
            ))}
          {activeIndex === 6 &&
            (counsellingType === '4' ? (
              <StepTableGrid />
            ) : (
              <NoStepPlaceholder message="No Table Grid" />
            ))}
          {activeIndex === 7 && <StepOrderTemplate />}
          {activeIndex === 8 &&
            (['1', '2', '9'].includes(counsellingType) ? (
              <StepSeniority />
            ) : (
              <NoStepPlaceholder message="No Seniority Order" />
            ))}

          {activeIndex === 9 && <StepConfirmation />}
        </div>
      </div>
    </PlanningWizardContext.Provider>
  );
};

export default PlanNewCounselling;

/* eslint-enable @typescript-eslint/no-explicit-any */
